// injuryHints.js — advisory-only injury/missed-games redistribution hints for
// the Projection Lab wizard. When the user lowers a named starter's games
// below the role mean, suggest where the vacated volume historically goes.
//
// Constants come from the vault weekly-redistribution study and are
// load-bearing — do not invent others:
//   RB: ~90% of vacated rush share stays in-group; the other RB absorbs
//       +53% of it, RB3+ depth +37%.
//   WR: depth (WR4+) absorbs +75% of vacated target share; WR2's measured
//       bump was +4.7% with a CI including 0 → explicitly NO WR2/WR3 bump.
//   TE: only ~52% of vacated target share returns, to TE2/depth at most.
//
// Like computeCompetitionHints, this is a pure read — it never mutates allocs
// and is never auto-applied. Do NOT feed these into default seeds,
// buildInitAllocs/Rates/Volume, or any scoring path (double-counting —
// vault correction).

import { GP_ROLE_MEANS } from "./projlabProjection";

const HINT_SLOTS = ["RB1", "RB2", "WR1", "WR2", "WR3", "TE1"];

// Share of the vacated volume that the study says gets redistributed,
// applied to the source slot's 17-game-pace share.
const RB_OTHER_FACTOR = 0.53;
const RB_DEPTH_FACTOR = 0.37;
const WR_DEPTH_FACTOR = 0.75;
const TE_DEPTH_FACTOR = 0.52;

const roundHalf = (v) => Math.round(v * 2) / 2;
const fmtPp = (v) => (v % 1 === 0 ? v.toFixed(0) : v.toFixed(1));

/**
 * computeInjuryHints(allocs) -> { [slotKey]: hint }
 * hint = { level: "warn"|"info", kind, gapGames, suggest }
 *
 * Fires on named slots whose stint games sum >=2 below the role mean
 * (GP_ROLE_MEANS); gap >= 4 escalates info -> warn. The hint attaches to the
 * SOURCE slot (the one whose games were lowered). Shares are 17-game pace, so
 * the vacated share is share × gap/17. Suggested bumps round to the nearest
 * 0.5pp; a hint whose primary bump rounds below 0.5pp is suppressed.
 */
export function computeInjuryHints(allocs) {
  const hints = {};

  for (const key of HINT_SLOTS) {
    const slot = allocs?.[key];
    if (!slot?.stints?.[0]?.player) continue;

    const totalGames = slot.stints.reduce((sum, st) => sum + (st.games ?? 17), 0);
    const gap = (GP_ROLE_MEANS[key] ?? 17) - totalGames;
    if (gap < 2) continue;

    const level = gap >= 4 ? "warn" : "info";
    const isRB = key.startsWith("RB");
    const share = isRB ? (slot.rushPct ?? 0) : (slot.tgtPct ?? 0);
    const vac = share * (gap / 17);

    let kind, suggest;
    if (isRB) {
      const primary = roundHalf(RB_OTHER_FACTOR * vac);
      if (primary < 0.5) continue;
      const depth = roundHalf(RB_DEPTH_FACTOR * vac);
      const other = key === "RB1" ? "RB2" : "RB1";
      kind = "rb-vacated";
      // depth can round to 0 while primary rounds to 0.5 (vac ≈ 0.47-0.67pp) — omit the clause
      suggest = depth < 0.5
        ? `${other} +${fmtPp(primary)}pp rush`
        : `${other} +${fmtPp(primary)}pp rush · RB3+ +${fmtPp(depth)}pp rush`;
    } else if (key.startsWith("WR")) {
      const primary = roundHalf(WR_DEPTH_FACTOR * vac);
      if (primary < 0.5) continue;
      kind = "wr-vacated";
      suggest = `WR4+ +${fmtPp(primary)}pp tgt (not WR2/WR3)`;
    } else {
      const primary = roundHalf(TE_DEPTH_FACTOR * vac);
      if (primary < 0.5) continue;
      kind = "te-vacated";
      suggest = `TE2/depth +${fmtPp(primary)}pp tgt max`;
    }

    hints[key] = { level, kind, gapGames: gap, suggest };
  }

  return hints;
}
